import {
  createRootRoute,
  createRoute,
  createRouter,
  notFound,
  redirect,
  type RouterHistory,
} from '@tanstack/react-router'
import { AppFrame } from '@/react/frame/AppFrame'
import { MapPage } from '@/react/frame/MapPage'
import { Leaving } from './Leaving'
import { decideAppEntry } from './appEntry'

export type AppSearch = { workspace_id?: number }

/** `?workspace_id=` as a non-negative integer; anything else counts as absent. */
export function toWorkspaceId(value: unknown): number | undefined {
  const n = typeof value === 'number' ? value : typeof value === 'string' && value !== '' ? Number(value) : NaN
  return Number.isInteger(n) && n >= 0 ? n : undefined
}

const rootRoute = createRootRoute({ component: AppFrame, notFoundComponent: Leaving })

export const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app',
  validateSearch: (search: Record<string, unknown>): AppSearch => ({
    workspace_id: toWorkspaceId(search.workspace_id),
  }),
  beforeLoad: ({ search }) => {
    const entry = decideAppEntry(search.workspace_id, localStorage)
    if (entry.kind === 'leave') throw notFound()
    if (entry.kind === 'enter-with-workspace') {
      throw redirect({ to: '/app', search: { workspace_id: entry.workspaceId }, replace: true })
    }
  },
  component: MapPage,
})

const demoRoute = createRoute({ getParentRoute: () => rootRoute, path: '/demo', component: MapPage })

// The Vue router's catch-all: every other path leaves. `/` itself never reaches
// the SPA — Flask serves it.
const anythingElse = createRoute({
  getParentRoute: () => rootRoute,
  path: '$',
  beforeLoad: () => {
    throw notFound()
  },
})

const routeTree = rootRoute.addChildren([appRoute, demoRoute, anythingElse])

export function createAppRouter(options: { history?: RouterHistory } = {}) {
  return createRouter({ routeTree, history: options.history, defaultNotFoundComponent: Leaving })
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>
  }
}
